import { onBeforeUnmount } from 'vue';
import { eventHub } from '@/lib/services/eventhub';

/**
 * Composable for modern EventHub usage with Composition API
 * Provides type-safe access to global event system
 */
export function useEventHub() {
	// Store cleanup functions for automatic cleanup
	const cleanupFunctions: Array<() => void> = [];

	/**
	 * Emit an event on the global event hub
	 */
	const emit = (eventName: string, data?: any) => {
		eventHub.$emit(eventName, data);
	};

	/**
	 * Listen to an event on the global event hub
	 * Automatically cleaned up on component unmount
	 */
	const on = (eventName: string, handler: (data: any) => void) => {
		eventHub.$on(eventName, handler);

		// Store cleanup function
		const cleanup = () => eventHub.$off(eventName, handler);
		cleanupFunctions.push(cleanup);

		return cleanup;
	};

	/**
	 * Listen to an event once on the global event hub
	 */
	const once = (eventName: string, handler: (data: any) => void) => {
		eventHub.$once(eventName, handler);
	};

	/**
	 * Remove event listener
	 */
	const off = (eventName: string, handler?: (data: any) => void) => {
		eventHub.$off(eventName, handler);
	};

	// Automatic cleanup on component unmount
	onBeforeUnmount(() => {
		cleanupFunctions.forEach((cleanup) => cleanup());
		cleanupFunctions.length = 0;
	});

	return {
		emit,
		on,
		once,
		off,
	};
}
